import { en } from "./catalogs/en";

export type MessageKey = keyof typeof en;
export type Catalog = { [K in MessageKey]: string };
export type LocaleCatalog = Partial<Catalog>;
type Placeholders<S extends string> =
  S extends `${string}{${infer P}}${infer Rest}`
    ? P | Placeholders<Rest>
    : never;
export type MessageParameters<K extends MessageKey> = Record<
  Placeholders<(typeof en)[K]>,
  string | number
>;
export type MessageArguments<K extends MessageKey> =
  keyof MessageParameters<K> extends never ? [] : [MessageParameters<K>];

export function placeholderNames(message: string): string[] {
  return [
    ...new Set([...message.matchAll(/\{([^{}]+)\}/g)].map((m) => m[1])),
  ].sort();
}

/** Missing/blank entries intentionally fall back. A populated entry must match. */
export function validateCatalog(catalog: LocaleCatalog): void {
  for (const key of Object.keys(catalog) as MessageKey[]) {
    const translated = catalog[key];
    if (!Object.hasOwn(en, key)) throw new Error(`Unknown message key: ${key}`);
    if (!translated?.trim()) continue;
    if (
      placeholderNames(translated).join() !== placeholderNames(en[key]).join()
    ) {
      throw new Error(`Placeholder mismatch: ${key}`);
    }
  }
}

export function formatMessage(
  template: string,
  parameters: Record<string, string | number>,
): string {
  return template.replace(/\{([^{}]+)\}/g, (token, name: string) =>
    Object.prototype.hasOwnProperty.call(parameters, name)
      ? String(parameters[name])
      : token,
  );
}
